import { MonetaryAmount, Shimmer } from '@components'
import type { LedgerReportAccount, ReportAttachmentsByDocument } from '@query'
import { reportAttachmentsFor } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EntryAttachmentAction } from './EntryAttachmentAction'
import { EntryPanelPagination } from './EntryPanelPagination'
import { LedgerEntryShimmerRows } from './EntryPanelShimmer'
import { ReportDocumentPreview } from './ReportDocumentPreview'
import {
  AttachmentSlot,
  DateCell,
  EntryHeaderRow,
  EntryPanel,
  EntryVoucherDescription,
  EntryVoucherNumber,
  EntryVoucherRow,
  ReportRow,
  RowLabel,
  ValueCell
} from './interactive.styled'

type Props = {
  account?: LedgerReportAccount
  attachmentsByDocument?: ReportAttachmentsByDocument
  currency?: string
  isLoading?: boolean
  entriesPage: number
  entriesPageCount: number
  entriesTotalCount: number
  entriesPageSize?: number
  onEntriesPageChange: (page: number) => void
}

export const AccountEntriesPanel: React.FC<Props> = ({
  account,
  attachmentsByDocument,
  currency,
  isLoading = false,
  entriesPage,
  entriesPageCount,
  entriesTotalCount,
  entriesPageSize,
  onEntriesPageChange
}) => {
  const [t] = useTranslation()
  // API pages newest-first; reverse so opening_balance sits above the oldest row it seeds.
  const entries = [...(account?.entries ?? [])].reverse()
  const showPagination = entriesTotalCount > 0

  return (
    <EntryPanel>
      <EntryHeaderRow>
        <AttachmentSlot />
        <DateCell $header>{t('reports.interactive.date')}</DateCell>
        <RowLabel $header>{t('reports.interactive.voucherDescription')}</RowLabel>
        <ValueCell $width="110px" $header>
          {t('reports.interactive.debit')}
        </ValueCell>
        <ValueCell $width="110px" $header>
          {t('reports.interactive.credit')}
        </ValueCell>
        <ValueCell $width="128px" $header>
          {t('reports.interactive.balance')}
        </ValueCell>
      </EntryHeaderRow>

      <ReportRow $entryRow $bg="#f7fafd">
        <AttachmentSlot />
        <DateCell />
        <EntryVoucherRow>
          <EntryVoucherDescription>
            {t('reports.interactive.openingBalance')}
          </EntryVoucherDescription>
        </EntryVoucherRow>
        <ValueCell $width="110px" />
        <ValueCell $width="110px" />
        <ValueCell $width="128px" $muted>
          {isLoading ? (
            <Shimmer width={72} height={12} borderRadius={4} />
          ) : (
            <MonetaryAmount value={account?.opening_balance ?? 0} currency={currency} />
          )}
        </ValueCell>
      </ReportRow>

      {isLoading ? (
        <LedgerEntryShimmerRows />
      ) : (
        entries.map((entry, index) => (
          <ReportRow key={`${entry.number}-${index}`} $entryRow $bg="#f7fafd">
            <EntryAttachmentAction
              attachments={reportAttachmentsFor(attachmentsByDocument, entry.document_id)}
            />
            <DateCell>{formatDate(entry.date)}</DateCell>
            <EntryVoucherRow>
              <EntryVoucherNumber>
                {entry.document_id ? (
                  <ReportDocumentPreview documentId={entry.document_id}>
                    {entry.number}
                  </ReportDocumentPreview>
                ) : (
                  entry.number
                )}
              </EntryVoucherNumber>
              {entry.description ? (
                <EntryVoucherDescription>{entry.description}</EntryVoucherDescription>
              ) : null}
            </EntryVoucherRow>
            <ValueCell $width="110px">
              {entry.debet ? <MonetaryAmount value={entry.debet} currency={currency} /> : null}
            </ValueCell>
            <ValueCell $width="110px">
              {entry.credit ? <MonetaryAmount value={entry.credit} currency={currency} /> : null}
            </ValueCell>
            <ValueCell $width="128px" $muted>
              <MonetaryAmount value={entry.balance_csum} currency={currency} />
            </ValueCell>
          </ReportRow>
        ))
      )}

      {showPagination && (
        <EntryPanelPagination
          page={entriesPage}
          pageCount={entriesPageCount}
          totalCount={entriesTotalCount}
          pageSize={entriesPageSize}
          onPageChange={onEntriesPageChange}
        />
      )}
    </EntryPanel>
  )
}
